// TODO: Assessment remote cleanup, delete this file. See "Cleanup" in docs/RemoteModules.md
import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { uniqBy } from 'lodash';
import { fetchAvailabilityCollectionPeriods } from 'slices/availabilityCollectionPeriod.slice';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { TAvailabilityCollectionEmbeddedTimeSlot } from 'types/availabilityCollectionPeriod.type';
import type { TAvailabilityCollectionTimeslot } from 'types/availabilityCollectionTimeslot.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { message } from 'utils/messageUtils';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { validateAvailabilityTimeslot } from 'utils/validations/availabilityCollectionSettings.validation';
import api from '../services/api.service';

export type TAvailabilityCollectionTimeslotState = IDefaultReducerState & {
  data: TAvailabilityCollectionTimeslot[];
  selectedTimeslot?: TAvailabilityCollectionTimeslot | null;
  confirmationData: TAvailabilityCollectionTimeslotConfirmationData;
};

export type TAvailabilityCollectionTimeslotConfirmationData = {
  updateRelatedPeriods: boolean;
};

const initialConfirmationData: TAvailabilityCollectionTimeslotConfirmationData =
  {
    updateRelatedPeriods: false,
  };

export const initialState: TAvailabilityCollectionTimeslotState = {
  loading: false,
  hasErrors: false,
  selectedTimeslot: null,
  data: [],
  confirmationData: initialConfirmationData,
};

// Slice
const slice = createSlice({
  name: 'availabilityCollectionMapping',
  initialState,
  reducers: {
    fetchAvailabilityCollectionTimeslotsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAvailabilityCollectionTimeslotsSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionTimeslot[]>
    ) => {
      state.data = payload;
      finishedLoadingSuccess(state);
    },
    fetchAvailabilityCollectionTimeslotsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createAvailabilityCollectionTimeslotRequest: (state) => {
      isLoadingRequest(state);
    },
    createAvailabilityCollectionTimeslotSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionTimeslot>
    ) => {
      state.data.push(payload);
      state.selectedTimeslot = null;
      finishedLoadingSuccess(state);
    },
    createAvailabilityCollectionTimeslotFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateAvailabilityCollectionTimeslotRequest: (state) => {
      isLoadingRequest(state);
    },
    updateAvailabilityCollectionTimeslotSuccess: (
      state,
      { payload }: PayloadAction<TAvailabilityCollectionTimeslot>
    ) => {
      const findIndex = state.data.findIndex(
        (item) => item._id === payload._id
      );
      if (findIndex !== -1) {
        state.data[findIndex] = payload;
        state.selectedTimeslot = null;
      }
      state.confirmationData = initialConfirmationData;
      finishedLoadingSuccess(state);
    },
    updateAvailabilityCollectionTimeslotFailure: (state) => {
      state.confirmationData = initialConfirmationData;
      finishedLoadingFailure(state);
    },
    deleteAvailabilityCollectionTimeslotRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteAvailabilityCollectionTimeslotSuccess: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.data = state.data.filter((item) => item._id !== payload);
      state.selectedTimeslot = null;
      state.confirmationData = initialConfirmationData;
      finishedLoadingSuccess(state);
    },
    deleteAvailabilityCollectionTimeslotFailure: (state) => {
      state.confirmationData = initialConfirmationData;
      finishedLoadingFailure(state);
    },
    createUnsaveTimeslot: (state) => {
      state.selectedTimeslot = {
        _id: 'new',
        name: '',
        startTime: 0,
        endTime: 120,
        weekdays: [],
        description: undefined,
      };
    },
    deleteUnsaveTimeslot: (state) => {
      state.selectedTimeslot = null;
    },
    resetTimeslotChanges: (state) => {
      if (!state.selectedTimeslot) return;

      if (state.selectedTimeslot._id === 'new') {
        state.selectedTimeslot = {
          _id: 'new',
          startTime: 0,
          endTime: 120,
          weekdays: [],
          name: '',
          description: undefined,
        };
        return;
      }

      const originalTimeslot = state.data.find(
        (item) => item._id === state.selectedTimeslot._id
      );

      if (originalTimeslot) {
        state.selectedTimeslot = { ...originalTimeslot };
        return;
      }
      state.selectedTimeslot = null;
    },
    setSelectedTimeslot: (state, { payload: timeslot }) => {
      state.selectedTimeslot = timeslot;
    },
    updatedSelectedTimeslot: (
      state,
      { payload }: PayloadAction<Partial<TAvailabilityCollectionTimeslot>>
    ) => {
      if (!state.selectedTimeslot) return;

      state.selectedTimeslot = {
        ...state.selectedTimeslot,
        ...payload,
      };
    },
    setAvailabilityCollectionTimeslotUpdateRelated: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.confirmationData.updateRelatedPeriods = payload;
    },
  },
});

export default slice.reducer;

// Selectors
export const availabilityCollectionTimeslotsSelector = (
  state: TRootState
): TAvailabilityCollectionTimeslot[] =>
  state.availabilityCollectionTimeslots.data;

export const availabilityCollectionTimeslotsLoadingSelector = (
  state: TRootState
): boolean => state.availabilityCollectionTimeslots.loading;

export const availabilityCollectionTimeslotsSelectedTimeslotSelector = (
  state: TRootState
): TAvailabilityCollectionTimeslot | null =>
  state.availabilityCollectionTimeslots.selectedTimeslot;

export const availabilityCollectionTimeslotsConfirmationDataSelector = (
  state: TRootState
): TAvailabilityCollectionTimeslotConfirmationData =>
  state.availabilityCollectionTimeslots.confirmationData;

export const availabilityCollectionPeriodsTimeslotsSelector = createSelector(
  (state: TRootState) =>
    state.availabilityCollectionPeriod.selectedPeriod?.timeSlots,
  (state: TRootState) => state.availabilityCollectionTimeslots.data,
  (
    periodSlots: TAvailabilityCollectionTimeslot[],
    globalSlots: TAvailabilityCollectionEmbeddedTimeSlot[]
  ) =>
    uniqBy(
      [...(periodSlots ?? []), ...(globalSlots ?? [])],
      (timeslot) => timeslot._id
    ).sort((a, b) => a.startTime - b.startTime)
);

// Actions
export const {
  fetchAvailabilityCollectionTimeslotsRequest,
  fetchAvailabilityCollectionTimeslotsSuccess,
  fetchAvailabilityCollectionTimeslotsFailure,
  createAvailabilityCollectionTimeslotRequest,
  createAvailabilityCollectionTimeslotSuccess,
  createAvailabilityCollectionTimeslotFailure,
  updateAvailabilityCollectionTimeslotRequest,
  updateAvailabilityCollectionTimeslotSuccess,
  updateAvailabilityCollectionTimeslotFailure,
  deleteAvailabilityCollectionTimeslotRequest,
  deleteAvailabilityCollectionTimeslotSuccess,
  deleteAvailabilityCollectionTimeslotFailure,
  createUnsaveTimeslot,
  deleteUnsaveTimeslot,
  updatedSelectedTimeslot,
  setSelectedTimeslot,
  resetTimeslotChanges,
  setAvailabilityCollectionTimeslotUpdateRelated,
} = slice.actions;

export const fetchAvailabilityCollectionTimeslots = createAppAsyncThunk(
  'availabilityCollectionSettings/fetchAvailabilityCollectionTimeslots',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAvailabilityCollectionTimeslotsRequest());
      const result = await api.get({
        endpoint: `/exam/availability-settings/timeslots`,
      });
      dispatch(fetchAvailabilityCollectionTimeslotsSuccess(result));
    } catch (e) {
      dispatch(fetchAvailabilityCollectionTimeslotsFailure());
      console.error(e);
    }
  }
);

export const createAvailabilityCollectionTimeslot = createAppAsyncThunk(
  'availabilityCollectionSettings/createAvailabilityCollectionTimeslot',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(createAvailabilityCollectionTimeslotRequest());
      const state = getState();
      const selectedTimeslot =
        state.availabilityCollectionTimeslots.selectedTimeslot;
      const validated = validateAvailabilityTimeslot(selectedTimeslot);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }
      const result = await api.post({
        endpoint: `/exam/availability-settings/timeslots`,
        data: {
          ...selectedTimeslot,
          _id: undefined, // Ensure the new timeslot does not have an ID
        },
      });
      dispatch(createAvailabilityCollectionTimeslotSuccess(result));
    } catch (e) {
      dispatch(createAvailabilityCollectionTimeslotFailure());
      console.error(e);
    }
  }
);

export const updateAvailabilityCollectionTimeslot = createAppAsyncThunk(
  'availabilityCollectionSettings/updateAvailabilityCollectionTimeslot',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(updateAvailabilityCollectionTimeslotRequest());
      const state = getState();
      const { _id, ...selectedTimeslot } =
        state.availabilityCollectionTimeslots.selectedTimeslot;

      const { updateRelatedPeriods } =
        state.availabilityCollectionTimeslots.confirmationData;

      const validated = validateAvailabilityTimeslot(selectedTimeslot);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }
      const result = await api.put({
        endpoint: `/exam/availability-settings/timeslots/${_id}?updateRelated=${updateRelatedPeriods}`,
        data: selectedTimeslot,
        suppressNotification: true,
      });
      const { relatedUpdates, ...timeslot } = result;

      if (
        relatedUpdates &&
        'warnings' in relatedUpdates &&
        relatedUpdates.warnings.length > 0
      ) {
        relatedUpdates.warnings.forEach((warning: string) => {
          message.warning(warning);
        });
      }

      if (!relatedUpdates?.updatedCount) {
        message.success('The operation was successful');
      } else {
        message.success(
          `Timeslots in periods ${result.relatedUpdates?.periods
            ?.map((period) => period.periodName)
            .join(', ')} updated.`
        );

        dispatch(fetchAvailabilityCollectionPeriods());
      }

      dispatch(updateAvailabilityCollectionTimeslotSuccess(timeslot));
    } catch (e) {
      dispatch(updateAvailabilityCollectionTimeslotFailure());
      console.error(e);
    }
  }
);

export const deleteAvailabilityCollectionTimeslot = createAppAsyncThunk(
  'availabilityCollectionSettings/deleteAvailabilityCollectionTimeslot',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(deleteAvailabilityCollectionTimeslotRequest());
      const state = getState();
      const { _id } = state.availabilityCollectionTimeslots.selectedTimeslot;
      const { updateRelatedPeriods } =
        state.availabilityCollectionTimeslots.confirmationData;
      await api.delete({
        endpoint: `/exam/availability-settings/timeslots/${_id}?updateRelated=${updateRelatedPeriods}`,
      });
      dispatch(fetchAvailabilityCollectionPeriods());
      dispatch(deleteAvailabilityCollectionTimeslotSuccess(_id));
    } catch (e) {
      dispatch(deleteAvailabilityCollectionTimeslotFailure());
      console.error(e);
    }
  }
);
